import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type ImageSourcePropType, ScrollView } from "react-native";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Container from "../Container";
import { OnboardingCategoryScreen } from "./OnboardingCategoryScreen";
import { OnboardingExamDateScreen } from "./OnboardingExamDateScreen";
import { OnboardingExamScreen } from "./OnboardingExamScreen";
import {
	OnboardingFooter,
	type OnboardingPrimaryAction,
} from "./OnboardingFooter";
import { OnboardingHeader } from "./OnboardingHeader";
import { OnboardingLearnerTypeScreen } from "./OnboardingLearnerTypeScreen";
import { OnboardingLevelScreen } from "./OnboardingLevelScreen";
import { OnboardingPlanScreen } from "./OnboardingPlanScreen";
import { OnboardingPrivacyScreen } from "./OnboardingPrivacyScreen";
import { OnboardingProfileScreen } from "./OnboardingProfileScreen";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingRankScreen } from "./OnboardingRankScreen";
import { OnboardingReviewScreen } from "./OnboardingReviewScreen";
import { OnboardingSkillScreen } from "./OnboardingSkillScreen";
import { OnboardingStateScreen } from "./OnboardingStateScreen";
import { OnboardingToeicLevelOverviewScreen } from "./OnboardingToeicLevelOverviewScreen";
import { OnboardingWelcomeScreen } from "./OnboardingWelcomeScreen";
import { resolveI18n } from "./helpers";

const OnboardingStepContent = () => {
	const { step } = useOnboarding();
	const { t, i18n } = useTranslation();
	const locale = i18n.language;
	const title = resolveI18n(step.titlei18n, locale);
	const description = resolveI18n(step.descriptioni18n, locale);

	switch (step.step) {
		case "welcome":
			return (
				<OnboardingWelcomeScreen
					title={title}
					description={description}
					data={step.data}
				/>
			);
		case "privacy":
			return (
				<OnboardingPrivacyScreen
					title={title}
					description={description}
					data={step.data}
				/>
			);
		case "profile":
			return (
				<OnboardingProfileScreen
					title={title || t("onboarding.steps.profile.title")}
					description={description || t("onboarding.steps.profile.description")}
				/>
			);
		case "learnerType":
			return (
				<OnboardingLearnerTypeScreen
					title={title}
					description={description}
					data={step.data}
				/>
			);
		case "chooseCategory":
			return (
				<OnboardingCategoryScreen title={title} description={description} />
			);
		case "chooseLevel":
			return <OnboardingLevelScreen title={title} description={description} />;
		case "chooseState":
			return <OnboardingStateScreen title={title} description={description} />;
		case "chooseExam":
		case "chooseContext":
			return <OnboardingExamScreen title={title} description={description} />;
		case "examDate":
		case "set-date":
			return (
				<OnboardingExamDateScreen title={title} description={description} />
			);
		case "rank":
			return (
				<OnboardingRankScreen
					title={title}
					description={description}
					data={step.data}
				/>
			);
		case "skill":
			return (
				<OnboardingSkillScreen
					title={title}
					description={description}
					data={step.data}
				/>
			);
		case "plan":
			return (
				<OnboardingPlanScreen
					title={title}
					description={description}
					data={step.data}
				/>
			);
		case "toeicLevelOverview":
			return <OnboardingToeicLevelOverviewScreen />;
		case "review":
			return <OnboardingReviewScreen title={title} description={description} />;
		default:
			return null;
	}
};

const resolvePrimaryAction = ({
	isFirstStep,
	isLastStep,
	isReviewStep,
	hasSelectedDiagnostic,
}: {
	isFirstStep: boolean;
	isLastStep: boolean;
	isReviewStep: boolean;
	hasSelectedDiagnostic: boolean;
}): OnboardingPrimaryAction => {
	if (isFirstStep) {
		return "start";
	}
	if (isLastStep) {
		return isReviewStep && hasSelectedDiagnostic ? "continue" : "finish";
	}
	return "continue";
};

type Props = {
	logo: {
		light: ImageSourcePropType;
		dark: ImageSourcePropType;
	};
	renderHeaderRight?: () => ReactNode;
};

export const OnboardingApplet = ({ logo, renderHeaderRight }: Props) => {
	const theme = useAppTheme();
	const {
		step,
		displayStepIndex,
		displayStepsTotal,
		isFirstStep,
		isLastStep,
		canGoNext,
		selectedDiagnostic,
		isCompletionPending,
		onGoToPreviousStep,
		onGoToNextStep,
	} = useOnboarding();

	const isWelcomeStep = step.step === "welcome";
	const isReviewStep = step.step === "review";
	const showProgress = !(isWelcomeStep || isReviewStep);
	const currentProgress =
		displayStepsTotal > 0 ? displayStepIndex / displayStepsTotal : 0;
	const primaryAction = resolvePrimaryAction({
		isFirstStep,
		isLastStep,
		isReviewStep,
		hasSelectedDiagnostic: Boolean(selectedDiagnostic),
	});

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<OnboardingHeader
				logo={logo}
				showProgress={showProgress}
				progress={currentProgress}
				displayStepIndex={displayStepIndex}
				displayStepsTotal={displayStepsTotal}
				renderRight={renderHeaderRight}
			/>
			<ScrollView contentContainerStyle={{ flexGrow: 1 }}>
				<Box flex={1} paddingVertical={24} paddingHorizontal={16}>
					<Container
						maxWidth={480}
						padding={false}
						containerStyle={{ flex: 1 }}
					>
						<OnboardingStepContent />
					</Container>
				</Box>
			</ScrollView>
			<OnboardingFooter
				showPrevious={!isFirstStep}
				primaryAction={primaryAction}
				canGoNext={canGoNext}
				loading={isCompletionPending && isLastStep}
				onPrevious={onGoToPreviousStep}
				onNext={onGoToNextStep}
			/>
		</Box>
	);
};
